/**
 * VSL Protection Service — cloaker unificado (uma proteção por vídeo).
 *
 * Faz a ponte entre o shape da UI (VSLProtection, camelCase, herdado do
 * protótipo) e o backend /api/vsl-protections (snake_case, tb_vsl_protection).
 * Idioma e continente não entram (fora do escopo da UI atual); o backend aceita
 * esses campos ausentes.
 */

import { api } from './api';
import { VSLProtection, UtmRule } from '@/components/cloaker/protection/types';

const BASE = '/vsl-protections';

// MySQL/MariaDB pode devolver colunas JSON como string crua; garante array.
function asArray(v: any): any[] {
  if (Array.isArray(v)) return v;
  if (typeof v === 'string') {
    try {
      const p = JSON.parse(v);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  return [];
}

function toApi(p: VSLProtection) {
  return {
    name: p.name,
    id_video: p.videoId,
    enabled: p.enabled,
    location_mode: p.locationMode,
    countries: p.countries || [],
    regions: p.regions || [],
    allowed_devices: p.allowedDevices || [],
    // Toggle desligado => sem regras UTM (o motor trata lista vazia como neutra).
    utm_rules: p.utmEnabled
      ? (p.utmRules || []).map((r) => ({
          parameter: r.parameter,
          operator: r.operator,
          value: r.value,
          action: r.action,
        }))
      : [],
    block_proxy_vpn: !!p.blockProxyVpn,
    url_key_enabled: !!p.urlKeyEnabled,
    url_key_name: p.urlKeyName || null,
    url_key_value: p.urlKeyValue || null,
    token_enabled: !!p.tokenEnabled,
    token_value: p.tokenValue || null,
    blocked_action: p.blockedAction || 'video',
    id_redirect_video: p.redirectVideoId || null,
    redirect_url: p.redirectUrl || null,
    blocked_message: p.blockedMessage || null,
  };
}

function fromApi(a: any): VSLProtection {
  const devices = asArray(a?.allowed_devices);
  const utmRules: UtmRule[] = asArray(a?.utm_rules).map((r: any, i: number) => ({
    id: r.id || `${a?.id_vsl_protection || 'p'}-utm-${i}`,
    parameter: r.parameter,
    operator: r.operator,
    value: r.value ?? '',
    action: r.action,
  }));

  return {
    id: a?.id_vsl_protection ?? '',
    name: a?.name ?? '',
    videoId: a?.id_video ?? null,
    enabled: a?.enabled !== false,
    locationMode: a?.location_mode ?? 'allow',
    countries: asArray(a?.countries),
    regions: asArray(a?.regions),
    utmEnabled: utmRules.length > 0,
    utmRules,
    urlKeyEnabled: !!a?.url_key_enabled,
    urlKeyName: a?.url_key_name ?? '',
    urlKeyValue: a?.url_key_value ?? '',
    tokenEnabled: !!a?.token_enabled,
    tokenValue: a?.token_value ?? '',
    allowedDevices: devices.length ? devices : ['desktop', 'mobile', 'tablet'],
    // Campos vestigiais (idioma fora de escopo) — mantidos só p/ o shape.
    languageMode: 'allow',
    languages: [],
    blockProxyVpn: !!a?.block_proxy_vpn,
    blockedAction: a?.blocked_action ?? 'video',
    redirectVideoId: a?.id_redirect_video ?? null,
    redirectUrl: a?.redirect_url ?? undefined,
    blockedMessage: a?.blocked_message ?? undefined,
    blockedToday: a?.blocked_count ?? 0,
    redirects: a?.redirect_count ?? 0,
    createdAt: a?.created_at ?? a?.createdAt ?? '',
    updatedAt: a?.updated_at ?? a?.updatedAt ?? '',
  };
}

export const vslProtectionService = {
  list: async (userId: string): Promise<VSLProtection[]> => {
    const res = await api.get(`${BASE}/${userId}`);
    return Array.isArray(res) ? res.map(fromApi) : [];
  },

  create: async (userId: string, p: VSLProtection): Promise<VSLProtection> => {
    const res = await api.post(`${BASE}/${userId}`, toApi(p));
    return fromApi(res);
  },

  update: async (userId: string, id: string, p: VSLProtection): Promise<void> => {
    await api.put(`${BASE}/${userId}/${id}`, toApi(p));
  },

  remove: async (userId: string, id: string): Promise<void> => {
    await api.delete(`${BASE}/${userId}/${id}`);
  },
}
